/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import {
  GeneralPlayer,
  Button,
  Tooltip,
  CustomSelect,
  toast,
} from "@/components";
import { fileProcessingStatusToText } from "@/utils/constants/uiConstants";
import { mediaStatusTooltipText, mediaStatusToTextAndColor } from "@/utils";
import { formatTimestampToAgo } from "../../utils";
import { handleCopyId } from "@/utils/utilFunctions/commmonFunctions";
import { getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { MediaModel } from "@/database";
import { FormikProps } from "formik";
import { RotateCw } from "lucide-react";

interface MediaDetailPlayerProps {
  currentMediaDetailsForEdit: MediaModel;
  isWizard?: boolean;
  isPlayerInitialized: boolean;
  setIsPlayerInitialized: (value: boolean) => void;
  thumbSent: string;
  embedUpdateKey?: string;
  formik: FormikProps<any>;
  statusOptions: Array<{ label: string; value: string }>;
  setIsSaveAndPublishClicked: (value: boolean) => void;
  isReplacing?: boolean;
  replaceAcceptExtensions?: string[] | null;
  onReplaceFileSelected?: (file: File) => void;
}

const MediaDetailPlayer: React.FC<MediaDetailPlayerProps> = ({
  currentMediaDetailsForEdit,
  isWizard,
  isPlayerInitialized,
  setIsPlayerInitialized,
  thumbSent,
  embedUpdateKey,
  formik,
  statusOptions,
  setIsSaveAndPublishClicked,
  isReplacing,
  replaceAcceptExtensions,
  onReplaceFileSelected,
}) => {
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replacingDots, setReplacingDots] = useState(".");

  const handleReplaceClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    replaceInputRef.current?.click();
  };

  useEffect(() => {
    if (!isReplacing) {
      setReplacingDots(".");
      return;
    }
    const interval = setInterval(() => {
      setReplacingDots((prev) => (prev.length >= 3 ? "." : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, [isReplacing]);

  const handleReplaceFileSelected = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (replaceAcceptExtensions) {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!replaceAcceptExtensions.includes(extension)) {
        toast.fail("Only video files are allowed for this replace");
        return;
      }
    }

    onReplaceFileSelected?.(file);
  };

  const isPremiereMedia =
    currentMediaDetailsForEdit?.media_ref?.rel === "premiere" ||
    currentMediaDetailsForEdit?.asset_type === "premiere";

  const premiereThumbResolved =
    (typeof formik.values.premiereThumb === "string"
      ? formik.values.premiereThumb
      : "") ||
    getThumbnailUrlUtil({
      media: currentMediaDetailsForEdit,
      id: currentMediaDetailsForEdit?.images?.premiere,
      source: currentMediaDetailsForEdit?.source,
    });

  const thumbForDisplay = isPremiereMedia
    ? premiereThumbResolved || thumbSent
    : thumbSent;

  const currentState =
    currentMediaDetailsForEdit.state < 0 ? 12 : currentMediaDetailsForEdit.state;

  const processingSubStates = [3, 7, 8, 9];

  const broadSteps = [
    { key: "created", label: "Created", states: [1] },
    { key: "uploading", label: "Uploading", states: [2] },
    { key: "processing", label: "Processing", states: processingSubStates },
  ];

  return (
    <div className={`${isWizard ? "mt-0" : "mt-3"} z-1`}>
      {currentMediaDetailsForEdit.state !== 10 ? (
        <div
          className="d-flex flex-column justify-content-center align-items-start border rounded-top light position-relative bg-dark-subtle p-3"
          style={{
            height: "100%",
            width: "100%",
            minHeight: "158px",
          }}
        >
          <div style={{ maxWidth: "280px" }}>
            {broadSteps.map((step, index) => {
              const isLast = index === broadSteps.length - 1;
              const isCompleted =
                currentState > Math.max(...step.states) && currentState !== 12;
              const isCurrent = step.states.includes(currentState);
              const isError = currentState === 12;
              const subIndex = step.states.indexOf(currentState);
              const fillPercent = isCompleted
                ? 100
                : isCurrent
                  ? Math.round(((subIndex + 1) / step.states.length) * 100)
                  : 0;
              const statusText = isError && isCurrent
                ? "Error"
                : isCompleted || (isCurrent && fillPercent === 100)
                  ? "Done"
                  : isCurrent
                    ? `${fillPercent}%`
                    : "Pending";
              return (
                <div key={step.key} className="d-flex">
                  <div
                    className="d-flex flex-column align-items-center mt-1"
                    style={{ width: "16px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor:
                          isError && isCurrent
                            ? "#dc3545"
                            : isCompleted || isCurrent
                              ? "#0d6efd"
                              : "#adb5bd",
                        flexShrink: 0,
                      }}
                    >
                      {(isCompleted || (isCurrent && fillPercent === 100 && !isError)) && (
                        <i
                          className="bi bi-check-lg text-white"
                          style={{ fontSize: "9px" }}
                        />
                      )}
                      {isCurrent && fillPercent < 100 && !isError && (
                        <span
                          className="spinner-border spinner-border-sm text-white"
                          style={{ width: "8px", height: "8px", borderWidth: "1.5px" }}
                          role="status"
                        />
                      )}
                    </div>
                    {!isLast && (
                      <div
                        style={{
                          width: "1px",
                          flex: 1,
                          minHeight: "36px",
                          backgroundColor: "#adb5bd",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: `${isCompleted ? 100 : isCurrent ? fillPercent : 0}%`,
                            backgroundColor: isError && isCurrent ? "#dc3545" : "#0d6efd",
                            transition: "height 0.3s ease",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="ms-3 d-flex gap-2">
                    <div
                      className={`fs-6 ${isError && isCurrent ? "text-danger" : ""}`}
                      style={{
                        fontWeight: isCurrent ? 700 : 400,
                        opacity: isCompleted || isCurrent ? 1 : 0.5,
                      }}
                    >
                      {step.label}
                    </div>
                    <div
                      className={isCurrent ? "text-primary" : "text-secondary"}
                      style={{ fontSize: "12px" }}
                    >
                      | {statusText}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : isPlayerInitialized ? (
        <GeneralPlayer
          id={currentMediaDetailsForEdit.id}
          videoUrl={
            currentMediaDetailsForEdit?.asset_type === "library" ||
            currentMediaDetailsForEdit?.content_type === 2
              ? currentMediaDetailsForEdit?.previewfile
              : undefined
          }
          autoPlay={2}
          thumbSent={thumbForDisplay}
          embedUpdateKey={embedUpdateKey}
          videoData={{ mid: currentMediaDetailsForEdit?.embed?.id }}
          remoteConfig={
            currentMediaDetailsForEdit?.embed?.id ||
            currentMediaDetailsForEdit?.asset_type === "library" ||
            currentMediaDetailsForEdit?.content_type === 2
              ? true
              : currentMediaDetailsForEdit?.simulive?.parentMediaId
                ? false
                : false
          }
          playerType={
            currentMediaDetailsForEdit?.embed?.type === "facebook"
              ? "fb"
              : currentMediaDetailsForEdit?.embed?.type === "hls"
                ? "hls"
                : currentMediaDetailsForEdit?.embed?.type === "youtube"
                  ? "yt"
                  : ""
          }
        />
      ) : (
        <div
          className="position-relative w-100"
          style={{
            paddingTop: "56.25%",
            backgroundColor: "#000",
            backgroundImage: thumbForDisplay
              ? `url(${thumbForDisplay})`
              : `url(${currentMediaDetailsForEdit?.images?.base}/${
                  currentMediaDetailsForEdit?.is_vertical === 0
                    ? currentMediaDetailsForEdit?.images?.thumb
                    : currentMediaDetailsForEdit?.images?.portrait
                }.jpg)`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            borderRadius: "4px",
            objectFit: "contain",
            backgroundRepeat: "no-repeat",
          }}
        >
          <button
            className="position-absolute top-50 start-50 translate-middle d-flex align-items-center gap-2"
            onClick={() => setIsPlayerInitialized(true)}
            title="Play"
          >
            <i
              className="bi bi-play-fill text-white"
              style={{ fontSize: "54px" }}
            />
          </button>
        </div>
      )}
      {!isWizard && (
        <div className="mediaInfo bgGrey p-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <Button
                type="outline-primary"
                onClick={(e) => handleCopyId(e, currentMediaDetailsForEdit.id)}
                tooltipText="Copy ID"
                style={{
                  textTransform: "none",
                }}
              >
                {currentMediaDetailsForEdit.state === 10
                  ? currentMediaDetailsForEdit?.id
                  : "SLIKE ID"}
              </Button>
              {currentMediaDetailsForEdit?.content_type === 2 && (
                <>
                  <input
                    ref={replaceInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleReplaceFileSelected}
                    accept={
                      replaceAcceptExtensions
                        ? replaceAcceptExtensions
                            .map((ext: string) => `.${ext}`)
                            .join(",")
                        : undefined
                    }
                  />
                  <Button
                    type="outline-primary"
                    isDisabled={isReplacing}
                    onClick={handleReplaceClick}
                    tooltipText="Replace media file"
                    style={{
                      textTransform: "none",
                    }}
                  >
                    <RotateCw size={14} className="me-1" />
                    {isReplacing ? (
                      <>
                        Replacing
                        <span
                          style={{
                            display: "inline-block",
                            width: 18,
                            textAlign: "left",
                          }}
                        >
                          {replacingDots}
                        </span>
                      </>
                    ) : (
                      "Replace"
                    )}
                  </Button>
                </>
              )}
            </div>
            {currentMediaDetailsForEdit?.content_type &&
              currentMediaDetailsForEdit?.content_type !== 2 && (
                <Tooltip
                  content={mediaStatusTooltipText(
                    currentMediaDetailsForEdit.media_status || 0
                  )}
                >
                  <span
                    className={`badge ${
                      mediaStatusToTextAndColor(
                        currentMediaDetailsForEdit.media_status || 0
                      ).statusColor
                    } fs-6`}
                  >
                    {
                      mediaStatusToTextAndColor(
                        currentMediaDetailsForEdit.media_status || 0
                      ).statusText
                    }
                  </span>
                </Tooltip>
              )}
          </div>
          <div className="d-flex flex-column gap-2 H">
            <div className="fs-6">
              <div className="text-secondary">Modified :</div>
              <span style={{ marginRight: "5px" }}>
                {formatTimestampToAgo(currentMediaDetailsForEdit.mtuser || 0)}
              </span>
              <span>{currentMediaDetailsForEdit?.mtbydata?.name}</span>
            </div>
          </div>
        </div>
      )}
      {isWizard &&
        currentMediaDetailsForEdit?.content_type &&
        currentMediaDetailsForEdit?.content_type !== 2 && (
          <CustomSelect
            label="Status"
            // placeholder="Draft"
            value={String(formik.values.media_state)}
            handleChange={(selectedOption: any) => {
              if (selectedOption && !Array.isArray(selectedOption)) {
                formik.setFieldValue("media_state", selectedOption.value);
                setIsSaveAndPublishClicked(true);
              }
            }}
            options={statusOptions}
            handleBlur={formik.handleBlur}
            error={formik.errors.media_state}
            touched={!!formik.touched.media_state}
            updatedOptions={statusOptions}
            className="statusField gap-3"
            maintainSelectHeight="20px"
            customStyling={{ border: "2px solid var(--bg-label-color)" }}
          />
        )}
    </div>
  );
};

export default MediaDetailPlayer;
